import { color as colorTokens } from '@spotify-internal/encore-foundation';

// Helper for any prop that behaves like `as` from styled-components (ie `component` in Button)

/**
 * Compatibility type to include all html attributes with a fix for the `as` prop collision.
 * TODO: improve the type definition for the `css` prop to better mimic styled-components `css` prop definition.
 * https://ghe.spotify.net/encore/web/issues/236
 */

// Compatibility type to include all svg attributes with a fix for the `as` prop collision

export function assertNever(_) {
  throw new Error('Unreachable value');
}

/**
 * Export Foundation tokens
 *
 * TODO: If Foundation merges this PR (https://ghe.spotify.net/encore/foundation/pull/239),
 * remove and re-export from Foundation.
 */

// Helper for validating whether a color string is a foundation token
export function isColorToken(color) {
  return color !== undefined && color in colorTokens;
}

/**
 * Helper for converting a token (in string px format) to an integer.
 * @example
 * pxToInt(spacer16) // returns 16
 *  */
export var pxToInt = function pxToInt(px) {
  return parseInt(px, 10);
};

/**
 * Construct a type with the properties of T where T's optional properties (K)
 * are required in the new type.
 */

/**
 * Utility type to make it easy to add documented pseudo state types to components.
 * @example
 * export type ComponentProps = StyledWrapperProps & Pick<PseudoStateProps, 'isHover' | 'isFocus'>;
 */

/*
 * Takes a Type and a subset of its properties and prefixes them with a "$"
 * Useful when needing to convert some React component props to be
 * styled-components "transient props"
 * See: https://styled-components.com/docs/api#transient-props
 */

/*
 * Takes a Type and a subset of its properties and prefixes them with a "$" and makes them required
 * Essentially a combination of `Require` and `Prefix$`
 */

/**
 * From Type, pick a set of properties and prefix them with a "$"
 * Essentially a combination of `Pick` and `Prefix$`
 */

/*
 * Takes a type T and tacks on string type while preserving autocomplete
 * Useful when you have a selection of string literals but
 * still want to allow an open string as well
 * See: https://github.com/microsoft/TypeScript/issues/29729
 */

/**
 * Backwards-compatibility type used in components that previously rendered Type and now render Text.
 */

var textVariantMapping = {
  bass: 'headlineLarge',
  forte: 'headlineMedium',
  brio: 'titleLarge',
  altoBrio: 'titleLarge',
  alto: 'titleMedium',
  canon: 'titleSmall',
  celloCanon: 'titleSmall',
  cello: 'bodyMedium',
  ballad: 'bodyMedium',
  balladBold: 'bodyMediumBold',
  viola: 'bodySmall',
  violaBold: 'bodySmallBold',
  mesto: 'bodySmall',
  mestoBold: 'bodySmallBold',
  finale: 'marginal',
  finaleBold: 'marginalBold',
  // variants below aren't 1:1 so we're mapping to the variant that preserves visual space (they've also been deprecated for ~5 years)
  // https://www.figma.com/proto/KzUo98tnaOuGXNg3rrcYB8/Font-requests?page-id=2205%3A4688&node-id=2646-5935&viewport=409%2C106%2C0.05&scaling=min-zoom&starting-point-node-id=2645%3A5754
  metronome: 'bodySmallBold',
  minuet: 'marginal',
  minuetBold: 'marginalBold'
};

/** Variant map used if the weight prop (deprecated) is used to request a bold or black weight. */
var textVariantMappingWithBold = {
  cello: 'bodyMediumBold',
  ballad: 'bodyMediumBold',
  viola: 'bodySmallBold',
  mesto: 'bodySmallBold',
  finale: 'marginalBold',
  metronome: 'bodySmallBold',
  minuet: 'marginalBold'
};

/** Takes a legacy Type variant and and weight returns the equivalent Text variant */
export function mapLegacyToTextVariant(variant, weight) {
  // If the variant is a valid Text variant, return it (we ignore the `weight` prop in this case).
  if (Object.values(textVariantMapping).includes(variant)) {
    return variant;
  }
  // Logic for handling the `weight` prop:
  // - weight="bold" or "black" are treated the same, we map to the bold or black variant at the same font size
  // - No weight or weight="book" -> return the new variant mapping
  if ((weight === 'black' || weight === 'bold') && Object.keys(textVariantMappingWithBold).includes(variant)) {
    return textVariantMappingWithBold[variant];
  }
  return textVariantMapping[variant];
}